import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useResetTokenValidation } from '../../hooks/useResetTokenValidation';
import { vendorAuthService } from '../../services/vendorAuthService';

// S3 asset URLs
const imgPattern5 = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/auth-bg-pattern.png';
const imgGroup1 = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/auth-illustration.svg';
const logoImg = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png';

const ResetPassword: React.FC = () => {
  usePageTitle('Reset Password');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoading: isValidating, validationError, hasValidated, isValidToken, vendorEmail } = useResetTokenValidation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }

    const token = searchParams.get('token');
    if (!token) {
      setError('No reset token found. Please use the link from your email.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await vendorAuthService.resetPassword(token, newPassword);
      
      if (response.success) {
        setIsSubmitted(true);
        // Redirect to login page after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleBackToSignIn = () => {
    navigate('/login');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#67314f',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Illustration - Hidden on mobile */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          width: { xs: 0, md: '66.67%' }, // 2/3 of the page
          height: '100%',
          backgroundImage: `url(${imgPattern5})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          component="img"
          src={imgGroup1}
          alt="Illustration"
          sx={{
            width: '752px',
            height: '601px',
            mixBlendMode: 'overlay'
          }}
        />
      </Box>

      {/* Reset Password Form Container */}
      <Box
        sx={{
          width: { xs: '100%', md: '33.33%' }, // 1/3 of the page
          height: '100%',
          backgroundColor: 'white',
          padding: { xs: '40px 24px', md: '80px' },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '32px', md: '40px' },
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            width: '120px',
            height: '94px',
            position: 'relative'
          }}
        >
          <Box
            component="img"
            src={logoImg}
            alt="Eventi Logo"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
            justifyContent: 'center'
          }}
        >
          {/* Title Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography
              sx={{
                fontFamily: '"HT Mosafer Rounded", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '32px', md: '44px' },
                lineHeight: 1.15,
                letterSpacing: '-0.44px',
                color: '#181818',
                margin: 0
              }}
            >
              Reset password
            </Typography>
            {!isSubmitted && (
              <Typography
                sx={{
                  fontFamily: '"HT Mosafer Rounded", sans-serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: 1.4,
                  color: '#181818',
                  margin: 0
                }}
              >
                Make up a new password for your account.
              </Typography>
            )}
          </Box>

          {/* Token Validation Error */}
          {hasValidated && validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          {/* Loading State */}
          {isValidating && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={40} sx={{ color: '#67314f' }} />
              <Typography sx={{ ml: 2, color: '#181818' }}>
                Validating reset token...
              </Typography>
            </Box>
          )}

          {/* Form or Success Message */}
          {isSubmitted ? (
            /* Success Message */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                alignItems: 'center',
                textAlign: 'center',
                padding: '32px 24px',
                backgroundColor: '#f8f9fa',
                borderRadius: '16px',
                border: '2px solid #e8f5e8'
              }}
            >
              <Box
                sx={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#28a745',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  color: 'white'
                }}
              >
                ✓
              </Box>
              <Typography
                sx={{
                  fontFamily: '"HT Mosafer Rounded", sans-serif',
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: 1.3,
                  color: '#28a745',
                  margin: 0
                }}
              >
                Password Updated Successfully!
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"HT Mosafer Rounded", sans-serif',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: 1.5,
                  color: '#181818',
                  margin: 0
                }}
              >
                Your password has been successfully updated!<br />
                You can now sign in with your new password.
              </Typography>
            </Box>
          ) : hasValidated && isValidToken && !isValidating ? (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}
            >
              {error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {error}
                </Alert>
              )}

              {/* Email Field (Read-only) */}
              <TextField
                fullWidth
                label="Email Address"
                value={vendorEmail || ''}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f6f8f8',
                    borderRadius: '12px',
                    height: '56px',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#f0f0f0',
                      color: '#666'
                    }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: '"HT Mosafer Rounded", sans-serif',
                    fontSize: '16px',
                    color: '#666'
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: '"HT Mosafer Rounded", sans-serif',
                    fontSize: '14px',
                    color: '#93989c',
                    '&.Mui-focused': {
                      color: '#93989c'
                    }
                  }
                }}
              />

              {/* New Password Field */}
              <TextField
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowNewPassword}
                        edge="end"
                        sx={{ color: '#181818' }}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f6f8f8',
                    borderRadius: '12px',
                    height: '56px',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: '"HT Mosafer Rounded", sans-serif',
                    fontSize: '16px',
                    color: newPassword ? '#181818' : '#93989c',
                    '&::placeholder': {
                      color: '#93989c',
                      opacity: 1
                    }
                  }
                }}
              />

              {/* Confirm New Password Field */}
              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowConfirmPassword}
                        edge="end"
                        sx={{ color: '#181818' }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f6f8f8',
                    borderRadius: '12px',
                    height: '56px',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: '"HT Mosafer Rounded", sans-serif',
                    fontSize: '16px',
                    color: confirmPassword ? '#181818' : '#93989c',
                    '&::placeholder': {
                      color: '#93989c',
                      opacity: 1
                    }
                  }
                }}
              />

              {/* Update Password Button */}
              <Button
                type="submit"
                fullWidth
                disabled={isLoading}
                sx={{
                  height: '56px',
                  backgroundColor: '#67314f',
                  borderRadius: '16px',
                  fontFamily: '"HT Mosafer Rounded", sans-serif',
                  fontWeight: 700,
                  fontSize: '16px',
                  letterSpacing: '-0.32px',
                  textTransform: 'none',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#5a2a42'
                  },
                  '&:disabled': {
                    backgroundColor: '#93989c',
                    color: 'white'
                  }
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  'Update password'
                )}
              </Button>
            </Box>
          ) : null}

          {/* Back to Sign In Button */}
          <Button
            fullWidth
            onClick={handleBackToSignIn}
            sx={{
              height: '56px',
              backgroundColor: 'transparent',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              color: '#67314f',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(103, 49, 79, 0.04)'
              }
            }}
          >
            Back to Sign In
          </Button>
        </Box>

        {/* Footer */}
        <Typography
          sx={{
            fontFamily: '"HT Mosafer Rounded", sans-serif',
            fontWeight: 400,
            fontSize: '13px',
            lineHeight: 1.4,
            letterSpacing: '-0.08px',
            color: '#181818',
            opacity: 0.4,
            textAlign: 'center'
          }}
        >
          © Eventi 2025
        </Typography>
      </Box>
    </Box>
  );
};

export default ResetPassword;
